import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Check, Sparkles, Target, Trophy, AlertTriangle, RotateCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Carosello Sponsor con card 3D FLIP.
 * NUOVA UX: solo scelta sponsor. La scommessa viene aggiunta DOPO nella dashboard.
 */
export default function SponsorCarousel({
  leagueId, teamId, category, sponsors,
  currentChoice, currentBudget = 0,
  isPhase1 = true, onChanged,
}) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => sponsors || [], [sponsors]);
  const current = list[idx];

  const minBet = 30;
  const defaultBet = Math.max(Number(current?.upfront_credits || 0), minBet);
  const [pickedBet, setPickedBet] = useState(defaultBet);
  useEffect(() => { setPickedBet(defaultBet); }, [current?.sponsor_id, defaultBet]);

  useEffect(() => {
    if (currentChoice && list.length) {
      const foundIdx = list.findIndex(s => s.sponsor_id === currentChoice.sponsor_id);
      if (foundIdx >= 0) setIdx(foundIdx);
    }
    setFlipped(false);
  }, [currentChoice, list, category]);

  useEffect(() => { setFlipped(false); }, [idx]);

  if (!list.length) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-900/40 p-8 text-center text-slate-400 text-sm" data-testid={`sponsor-empty-${category}`}>
        Nessuno sponsor disponibile in questa categoria. L&apos;admin deve prima attivarli dalla sezione <span className="font-bold text-slate-300">Decisioni Admin</span>.
      </div>
    );
  }

  const isChosen = currentChoice && current && current.sponsor_id === currentChoice.sponsor_id;
  const catLabel = category === "bonus_initial" ? "Sponsor Bonus" : "Sponsor Pagato";
  const isBonus = category === "bonus_initial";
  const accent = isBonus ? "#10b981" : "#f59e0b";
  const softBg = isBonus ? "rgba(16,185,129,0.10)" : "rgba(245,158,11,0.10)";
  const softBorder = isBonus ? "rgba(16,185,129,0.35)" : "rgba(245,158,11,0.35)";

  const next = () => setIdx(i => (i + 1) % list.length);
  const prev = () => setIdx(i => (i - 1 + list.length) % list.length);
  const flip = () => setFlipped(f => !f);

  const cost = !isBonus ? Number(pickedBet || defaultBet) : 0;
  const insufficientFunds = cost > Number(currentBudget || 0);

  const accept = async () => {
    if (!isPhase1) { toast.error("La scelta sponsor è chiusa in questa fase"); return; }
    if (!isBonus && cost < minBet) {
      toast.error(`Scommessa minima ${minBet}c`);
      return;
    }
    if (insufficientFunds) {
      toast.error(`Budget insufficiente: servono ${cost}c, hai ${Math.floor(currentBudget)}c`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/leagues/${leagueId}/teams/${teamId}/sponsorships`, {
        sponsor_id: current.sponsor_id,
        category,  // richiesto per auto-clone template piattaforma
        bet_amount: isBonus ? 0 : pickedBet,
      });
      toast.success(`✨ Sponsor "${current.name}" attivato!${!isBonus ? ` Scommessa ${pickedBet}c.` : ""}`);
      onChanged?.(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!currentChoice) return;
    if (!window.confirm(`Rimuovere lo sponsor "${currentChoice.sponsor_name}"?`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/leagues/${leagueId}/teams/${teamId}/sponsorships/${currentChoice.sponsorship_id}`);
      toast.success(`Sponsor rimosso. Rimborso: ${r.data.refund}c`);
      onChanged?.(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl border p-5 relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #0f172a 0%, #020617 100%)",
        borderColor: softBorder,
      }}
      data-testid={`sponsor-carousel-${category}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: accent }}/>
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{catLabel}</span>
        </div>
        <span className="text-[10px] font-mono text-slate-500">{idx + 1} / {list.length}</span>
      </div>

      {/* Flip card */}
      <div className="mb-4" style={{ perspective: "1400px" }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            minHeight: 560,
            transformStyle: "preserve-3d",
            transition: "transform 700ms cubic-bezier(.65,.05,.36,1)",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          {/* FRONT */}
          <div
            className="absolute inset-0 rounded-3xl border p-5 flex flex-col items-center justify-between gap-3 text-center"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              background: `radial-gradient(1000px 500px at 50% -100px, ${softBg}, transparent 60%), #0b1220`,
              borderColor: softBorder,
              boxShadow: `0 12px 60px -20px ${accent}55`,
            }}
          >
            {/* Logo — BIG, senza bg (immagine ora trasparente) */}
            {current.logo_url && (
              <div className="relative w-full flex items-center justify-center pt-1">
                <div className="absolute inset-x-8 top-2 h-40 rounded-full blur-3xl opacity-30" style={{ background: accent }}/>
                <img src={current.logo_url} alt={current.name}
                  className="relative w-44 h-44 sm:w-52 sm:h-52 object-contain drop-shadow-2xl"
                  style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.5))" }}/>
              </div>
            )}

            {/* Nome + descrizione */}
            <div>
              <div className="font-black text-2xl sm:text-3xl text-white leading-tight tracking-tight">{current.name}</div>
              {current.tagline && <div className="text-xs italic text-slate-400 mt-1 max-w-md">&ldquo;{current.tagline}&rdquo;</div>}
              {current.description && <div className="text-[11px] text-slate-300 mt-2 max-w-md leading-relaxed">{current.description}</div>}
            </div>

            {/* OBIETTIVO — protagonista sulla facciata anteriore */}
            {current.objective_desc && (
              <div className="w-full rounded-2xl px-4 py-3 border text-left"
                style={{ background: softBg, borderColor: softBorder }}>
                <div className="text-[9px] uppercase tracking-[0.25em] font-black flex items-center gap-1.5 mb-1"
                  style={{ color: accent }}>
                  <Target size={11}/> Obiettivo
                </div>
                <div className="text-[13px] leading-snug text-slate-100 font-medium">{current.objective_desc}</div>
              </div>
            )}

            {/* Crediti — BIG. Premio BASE (fisso). La percentuale scala in base al piazzamento. */}
            {/* Iter 67: rimosso ramo bonus alla firma (upfront_credits). Ogni sponsor è ad obiettivo:
                normale (scommessa + premio) o finale (scommessa raddoppio + premio). */}
            <div className="w-full flex items-center justify-around gap-3 pt-1">
                <div className="flex flex-col items-center" data-testid={`sponsor-bet-picker-${category}`}>
                  <div className="text-[9px] uppercase tracking-[0.25em] font-black text-slate-500">Scommessa</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <button type="button" onClick={() => setPickedBet(v => Math.max(minBet, v - 10))}
                      disabled={pickedBet <= minBet}
                      className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 text-slate-300 font-black text-sm hover:bg-slate-700 disabled:opacity-30">−</button>
                    <div className="text-3xl sm:text-4xl font-black tabular-nums leading-none px-2" style={{ color: accent }}>
                      −{pickedBet}<span className="text-lg">c</span>
                    </div>
                    <button type="button" onClick={() => setPickedBet(v => v + 10)}
                      className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 text-slate-300 font-black text-sm hover:bg-slate-700">+</button>
                  </div>
                  {/* Quick presets 30 / 50 / 100 / 200 */}
                  <div className="flex gap-1 mt-1.5" data-testid={`sponsor-bet-presets-${category}`}>
                    {[30, 50, 100, 200].map(v => (
                      <button key={v} type="button" onClick={() => setPickedBet(v)}
                        data-testid={`sponsor-bet-preset-${v}`}
                        className={`text-[10px] font-black font-mono px-2 py-0.5 rounded border transition-all ${
                          pickedBet === v
                            ? "bg-amber-500 border-amber-400 text-slate-950 shadow-md"
                            : "bg-slate-900 border-slate-700 text-slate-400 hover:border-amber-500/50 hover:text-amber-300"
                        }`}>
                        {v}
                      </button>
                    ))}
                  </div>
                  <div className="text-[9px] tracking-[0.15em] text-slate-500 mt-1 uppercase text-center">
                    Modificabile · min {minBet}c
                  </div>
                </div>
              {current.prize_credits > 0 && (
                <div className="flex flex-col items-center">
                  <div className="text-[9px] uppercase tracking-[0.25em] font-black text-slate-500 flex items-center gap-1">
                    <Trophy size={9}/> Premio base
                  </div>
                  <div className="text-3xl sm:text-4xl font-black tabular-nums leading-none mt-1 text-emerald-300">
                    {current.prize_credits}<span className="text-lg">c</span>
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500 mt-1 text-center leading-tight">
                    modulato dal piazzamento<br/>
                    <span className="text-emerald-400/70 font-mono">
                      {Math.round(current.prize_credits * (current.default_percent || 50) / 100)}c
                      {' → '}
                      {Math.round(current.prize_credits * 1.5)}c
                    </span>
                  </div>
                </div>
              )}
            </div>

            <button onClick={flip} data-testid={`sponsor-flip-${category}`}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-300 hover:text-white transition-colors">
              <RotateCw size={11}/> Descrizione & scala premi
            </button>
          </div>

          {/* BACK */}
          <div
            className="absolute inset-0 rounded-3xl border p-6 flex flex-col gap-3"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              background: `linear-gradient(160deg, #0b1220 0%, ${isBonus ? "#052e28" : "#3a2a06"} 100%)`,
              borderColor: softBorder,
              boxShadow: `0 12px 60px -20px ${accent}55`,
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Dettagli sponsor</span>
              <button onClick={flip}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-300 hover:text-white">
                <RotateCw size={11}/> Fronte
              </button>
            </div>
            {current.description && (
              <p className="text-sm text-slate-300 leading-relaxed">{current.description}</p>
            )}
            {current.objective_desc && (
              <div className="flex items-start gap-2 text-xs rounded-xl px-3 py-2"
                style={{ background: softBg, border: `1px solid ${softBorder}` }}>
                <Target size={13} style={{ color: accent }} className="shrink-0 mt-0.5"/>
                <div><span className="font-bold text-white">Obiettivo:</span> <span className="text-slate-200">{current.objective_desc}</span></div>
              </div>
            )}
            {(current.prize_scale && current.prize_scale.length > 0) && (
              <div className="text-[11px]">
                <div className="text-slate-400 font-bold uppercase tracking-[0.15em] text-[10px] mb-1.5">Premio in base al piazzamento (classifica annuale)</div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                  {current.prize_scale.slice().sort((a,b)=>a.position_max-b.position_max).map((t, i) => {
                    const credits = Math.round(current.prize_credits * t.percent / 100);
                    return (
                      <div key={i} className="rounded-lg bg-slate-900/70 border border-slate-800 px-1.5 py-1.5 text-center">
                        <div className="font-mono text-slate-300 text-[10px]">≤{t.position_max}°</div>
                        <div className="font-black text-base" style={{ color: accent }}>{credits}c</div>
                        <div className="font-mono text-slate-500 text-[9px]">{t.percent}%</div>
                      </div>
                    );
                  })}
                  <div className="rounded-lg bg-slate-900/70 border border-slate-800 px-1.5 py-1.5 text-center">
                    <div className="font-mono text-slate-500 text-[10px]">altre pos.</div>
                    <div className="font-black text-base text-slate-400">{Math.round(current.prize_credits * (current.default_percent || 50) / 100)}c</div>
                    <div className="font-mono text-slate-500 text-[9px]">{current.default_percent}%</div>
                  </div>
                </div>
              </div>
            )}
            {current.penalty_credits > 0 && (
              <div className="text-xs rounded-xl px-3 py-2 bg-rose-500/10 border border-rose-500/30 flex items-start gap-2">
                <AlertTriangle size={13} className="text-rose-300 shrink-0 mt-0.5"/>
                <div><span className="font-bold text-white">Penalità se obiettivo mancato:</span> <span className="text-rose-200">−{current.penalty_credits}c</span></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nav + Accept */}
      <div className="flex items-center justify-between gap-3">
        <button onClick={prev} disabled={list.length < 2 || busy} data-testid={`sponsor-prev-${category}`}
          className="p-2.5 rounded-full bg-slate-800 hover:bg-slate-700 disabled:opacity-30 transition-colors">
          <ChevronLeft size={18}/>
        </button>

        {isChosen ? (
          <div className="flex items-center gap-2 flex-1 justify-center flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 font-black uppercase text-[11px] tracking-[0.2em]">
              <Check size={14}/> Attivo
            </div>
            {isPhase1 && (
              <button onClick={cancel} disabled={busy}
                data-testid={`sponsor-cancel-${category}`}
                className="text-[11px] font-bold uppercase tracking-[0.2em] text-rose-300 hover:text-rose-200 underline underline-offset-2 disabled:opacity-40">
                Rimuovi
              </button>
            )}
          </div>
        ) : (
          <button onClick={accept} disabled={!isPhase1 || busy || insufficientFunds}
            data-testid={`sponsor-accept-${category}`}
            className={`flex-1 max-w-xs px-6 py-3 rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all disabled:opacity-40 shadow-xl ${
              isBonus
                ? "bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-900 shadow-emerald-500/30 hover:scale-[1.03]"
                : "bg-gradient-to-br from-amber-300 to-amber-500 text-slate-900 shadow-amber-500/30 hover:scale-[1.03]"
            }`}>
            {busy ? "Attivo…"
              : insufficientFunds ? `Servono ${cost}c`
              : "✨ Accetta Sponsor"}
          </button>
        )}

        <button onClick={next} disabled={list.length < 2 || busy} data-testid={`sponsor-next-${category}`}
          className="p-2.5 rounded-full bg-slate-800 hover:bg-slate-700 disabled:opacity-30 transition-colors">
          <ChevronRight size={18}/>
        </button>
      </div>

      {/* Dots */}
      {list.length > 1 && list.length <= 24 && (
        <div className="mt-4 flex items-center justify-center gap-1.5 flex-wrap">
          {list.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6" : "w-1.5 bg-slate-700 hover:bg-slate-600"}`}
              style={i === idx ? { backgroundColor: accent } : undefined}
              aria-label={`Sponsor ${i + 1}`}/>
          ))}
        </div>
      )}

      <div className="mt-3 text-center text-[10px] text-slate-500 font-mono uppercase tracking-[0.2em]">
        Alla scelta: −{pickedBet}c dal team (scommessa)
      </div>
    </div>
  );
}
